import { Button, Panel, Typography, Skeleton } from "@/components/atoms";
import { EmptyState, Pagination } from "@/components/molecules";
import { useOperator, useOperatorAccess, useProfile } from "@/hooks";
import { usePagination } from "@/hooks/usePagination";
import { useGetUserAccountsQuery } from "@/redux/apis/userAccount/userAccountApi";
import { useAppSelector } from "@/redux/hooks";
import { userAccountsPageSelector, userAccountsSelector } from "@/redux/slices/userAccount/selectors";
import { UserAccount } from "@/redux/slices/userAccount/types";
import { UserAccountsEmptyState } from "./UserAccountsEmptyState";
import { UserAccountsTable } from "./UserAccountsTable";
import { useUserAccountsContext } from "./contexts/userAccounts";

export const UserAccountsPanel = (props: React.HTMLAttributes<HTMLDivElement>) => {
  const { isUnsubscribed, isSolo } = useOperatorAccess();

  // Solo and unsubscribed operators get a read-only, owner-focused view (no staff management).
  if (isUnsubscribed || isSolo) return <LimitedUserAccountsPanel {...props} />;

  return <FullUserAccountsPanel {...props} />;
};

const FullUserAccountsPanel = (props: React.HTMLAttributes<HTMLDivElement>) => {
  const { page, pageSize, handlePageClick, handleSizeChange } = usePagination();
  const { current, count, total, size } = useAppSelector(userAccountsPageSelector);
  const userAccounts = useAppSelector(userAccountsSelector);
  const { isFetching } = useGetUserAccountsQuery({ page, page_size: pageSize }, { refetchOnMountOrArgChange: true });

  if (isFetching) return <Skeleton />;

  return (
    <>
      <Panel {...props}>
        <Header />
        {userAccounts.length < 1 && !isFetching ? <UserAccountsEmptyState /> : <UserAccountsTable items={userAccounts} className="mt-4" />}
      </Panel>
      {!isFetching && (
        <Pagination
          onPageClick={handlePageClick}
          onSizeChange={handleSizeChange}
          currentPage={current}
          pageSize={size}
          totalItems={total}
          pageCount={count}
        />
      )}
    </>
  );
};

/**
 * Read-only directory for solo / unsubscribed operators: owner account only (no other staff, no
 * Invite/manage actions), so they can confirm their login details without full directory access.
 */
const LimitedUserAccountsPanel = (props: React.HTMLAttributes<HTMLDivElement>) => {
  const operator = useOperator();
  const profile = useProfile();
  const { data, isFetching } = useGetUserAccountsQuery({ page: "1", page_size: "100" }, { refetchOnMountOrArgChange: true });

  if (isFetching) return <Skeleton />;

  // Primary source: the provider-scoped directory (the operator's owner, regardless of who's viewing).
  const ownerFromDirectory = data?.data.items.find((item) => (item.role as string) === "Owner");

  // Fallback when the directory call is blocked (owner viewing their own inactive account 403s the
  // proxy). The owner's email is authoritative from the operator payload; if the logged-in viewer IS
  // the owner, their profile supplies name + phone too.
  const ownerEmail = operator.ownerEmail;
  const viewerIsOwner = Boolean(ownerEmail) && profile.email === ownerEmail;
  const ownerFallback: UserAccount | undefined = ownerEmail
    ? {
        id: "owner",
        name: viewerIsOwner ? profile.displayName || `${profile.firstName} ${profile.lastName}`.trim() || "-" : "-",
        status: "active",
        phone: viewerIsOwner ? profile.mobile || "-" : "-",
        email: ownerEmail,
        role: "Owner" as UserAccount["role"],
      }
    : undefined;

  const owner = ownerFromDirectory ?? ownerFallback;
  const items = owner ? [owner] : [];

  return (
    <Panel {...props}>
      <div className="flex flex-col gap-1">
        <Typography variant="h3" className="leading-8">
          User Accounts
        </Typography>
        <Typography className="text-neutral-dark-gray">Your owner account login details.</Typography>
      </div>
      {items.length < 1 ? (
        <EmptyState title="No account details to show" description="Your owner account login details will appear here once available." />
      ) : (
        <UserAccountsTable items={items} hideActions hideStatus className="mt-4" />
      )}
    </Panel>
  );
};

const Header = () => {
  const { setStep } = useUserAccountsContext();
  const userAccounts = useAppSelector(userAccountsSelector);
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="flex-1">
        <Typography variant="h3" className="leading-8">
          Staff Directory
        </Typography>
        <Typography className="text-neutral-dark-gray">Manage all the user permissions of Rideminder</Typography>
      </div>
      <div className="flex items-center">
        {userAccounts.length > 0 && (
          <Button onClick={() => setStep("add")} startIcon="Add" className="w-full sm:w-fit">
            Invite New
          </Button>
        )}
      </div>
    </div>
  );
};
